import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './lib/auth';
import AppShell from './components/AppShell';
import ChatWidget from './components/ChatWidget';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Discovery from './pages/Discovery';
import Portfolio from './pages/Portfolio';
import Backtest from './pages/Backtest';
import Learning from './pages/Learning';
import Alerts from './pages/Alerts';
import Audit from './pages/Audit';
import Manual from './pages/Manual';
import Settings from './pages/Settings';
import Access from './pages/Access';
import AutoTrading from './pages/AutoTrading';
import Sim from './pages/Sim';
import Truth from './pages/Truth';
import News from './pages/News';
import Log from './pages/Log';
import Notes from './pages/Notes';
import GlobePage from './pages/Globe';
import Lab from './pages/Lab';
import Crypto from './pages/Crypto';
// Phase 48 — AltMarkets import removed (product line discontinued).
import ErrorBoundary from './components/ErrorBoundary';
import BootGate from './components/BootGate';
import { useOperatorActivity } from './lib/operatorActivity';
import './App.css';

function Protected({ children }) {
  const { user, ready } = useAuth();
  useOperatorActivity(); // Phase 28A — operator activity heartbeat (runs only when authed)
  if (!ready) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-cyan-sys font-mono text-sm">// booting operator terminal...</div>
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  return (
    <AppShell>
      <ErrorBoundary>{children}</ErrorBoundary>
      <ChatWidget />
    </AppShell>
  );
}

function AuthShell() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/" element={<Navigate to="/app/dashboard" replace />} />
        <Route path="/app" element={<Navigate to="/app/dashboard" replace />} />
        <Route path="/app/dashboard" element={<Protected><Dashboard /></Protected>} />
        <Route path="/app/discovery" element={<Protected><Discovery /></Protected>} />
        <Route path="/app/portfolio" element={<Protected><Portfolio /></Protected>} />
        <Route path="/app/backtest"  element={<Protected><Backtest /></Protected>} />
        <Route path="/app/learning"  element={<Protected><Learning /></Protected>} />
        <Route path="/app/sim"       element={<Protected><Sim /></Protected>} />
        <Route path="/app/truth"     element={<Protected><Truth /></Protected>} />
        <Route path="/app/news"      element={<Protected><News /></Protected>} />
        <Route path="/app/log"       element={<Protected><Log /></Protected>} />
        <Route path="/app/alerts"    element={<Protected><Alerts /></Protected>} />
        <Route path="/app/audit"     element={<Protected><Audit /></Protected>} />
        <Route path="/app/manual"    element={<Protected><Manual /></Protected>} />
        <Route path="/app/notes"     element={<Protected><Notes /></Protected>} />
        <Route path="/app/globe"     element={<Protected><GlobePage /></Protected>} />
        <Route path="/app/lab"       element={<Protected><Lab /></Protected>} />
        <Route path="/app/crypto"    element={<Protected><Crypto /></Protected>} />
        {/* Phase 48 — AltMarkets removed: redirect to dashboard for any
            existing bookmarks or external links. */}
        <Route path="/app/alt"          element={<Navigate to="/app/dashboard" replace />} />
        <Route path="/app/alt-markets"  element={<Navigate to="/app/dashboard" replace />} />
        <Route path="/app/settings"  element={<Protected><Settings /></Protected>} />
        <Route path="/app/access"    element={<Protected><Access /></Protected>} />
        <Route path="/app/auto-trading" element={<Protected><AutoTrading /></Protected>} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </AuthProvider>
  );
}

export default function App() {
  return (
    <BootGate>
      <ErrorBoundary>
        <BrowserRouter>
          <AuthShell />
          <Toaster
            theme="dark"
            position="top-right"
            toastOptions={{
              style: {
                background: '#0A0A0A',
                border: '1px solid #08F7FE',
                color: '#E0E0E0',
                fontFamily: 'JetBrains Mono, monospace',
                borderRadius: 0,
              },
            }}
          />
        </BrowserRouter>
      </ErrorBoundary>
    </BootGate>
  );
}
